import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import {
  type ArcResult,
  crossVerdict,
  incomeVerdict,
  pointElasticity,
  priceVerdict,
  revenueEffect,
} from "@/lib/tool-kit/econ-micro/elasticity";
import { minus } from "@/lib/tool-kit/econ-micro/format";

type Measure = "price" | "income" | "cross";
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const STRING_KEYS = [
  "elasticityPrice",
  "elasticityIncome",
  "elasticityCross",
  "absoluteValue",
  "percentChangeQ",
  "percentChangePrice",
  "percentChangeIncome",
  "percentChangeCross",
  "verdictLabel",
  "verdictPerfectlyInelastic",
  "verdictInelastic",
  "verdictUnitElastic",
  "verdictElastic",
  "verdictInferior",
  "verdictNecessity",
  "verdictLuxury",
  "verdictUnitIncome",
  "verdictSubstitutes",
  "verdictComplements",
  "verdictUnrelated",
  "explainPerfectlyInelastic",
  "explainInelastic",
  "explainUnitElastic",
  "explainElastic",
  "explainInferior",
  "explainNecessity",
  "explainLuxury",
  "explainUnitIncome",
  "explainSubstitutes",
  "explainComplements",
  "explainUnrelated",
  "positiveNote",
  "workingHeading",
  "workQ",
  "workX",
  "workE",
  "roundedNote",
  "revenueHeading",
  "revenueMeasure",
  "revenueBefore",
  "revenueAfter",
  "revenueChange",
  "revenuePriceUpUp",
  "revenuePriceUpDown",
  "revenuePriceUpSame",
  "revenuePriceDownUp",
  "revenuePriceDownDown",
  "revenuePriceDownSame",
  "pointHeading",
  "pointIntro",
  "pointStart",
  "pointMid",
  "pointEnd",
  "pointUndefined",
  "pointNote",
] as const;

export type ElasticityWorkingStrings = Record<
  (typeof STRING_KEYS)[number],
  string
>;

interface Data {
  x1: number;
  x2: number;
  q1: number;
  q2: number;
}

/** The verdict name and one-sentence explanation for the elasticity just found. */
function verdictFor(
  measure: Measure,
  e: number,
  s: ElasticityWorkingStrings,
  fmt: Fmt
): { name: string; explain: string } {
  const abs = fmt(Math.abs(e), { maximumFractionDigits: 2 });
  if (measure === "price") {
    const table = {
      perfectlyInelastic: [
        s.verdictPerfectlyInelastic,
        s.explainPerfectlyInelastic,
      ],
      inelastic: [s.verdictInelastic, s.explainInelastic],
      unitElastic: [s.verdictUnitElastic, s.explainUnitElastic],
      elastic: [s.verdictElastic, s.explainElastic],
    } as const;
    const [name, explain] = table[priceVerdict(e)];
    return { name, explain: t(explain, { abs }) };
  }
  if (measure === "income") {
    const table = {
      inferior: [s.verdictInferior, s.explainInferior],
      necessity: [s.verdictNecessity, s.explainNecessity],
      luxury: [s.verdictLuxury, s.explainLuxury],
      unitIncome: [s.verdictUnitIncome, s.explainUnitIncome],
    } as const;
    const [name, explain] = table[incomeVerdict(e)];
    return { name, explain };
  }
  const table = {
    substitutes: [s.verdictSubstitutes, s.explainSubstitutes],
    complements: [s.verdictComplements, s.explainComplements],
    unrelated: [s.verdictUnrelated, s.explainUnrelated],
  } as const;
  const [name, explain] = table[crossVerdict(e)];
  return { name, explain };
}

function RevenueTest({
  data,
  fmt,
  strings: s,
}: {
  data: Data;
  fmt: Fmt;
  strings: ElasticityWorkingStrings;
}) {
  // Price is x, quantity is q.
  const tr = revenueEffect(data.x1, data.q1, data.x2, data.q2);
  const priceUp = data.x2 > data.x1;
  const sentences = {
    up: {
      up: s.revenuePriceUpUp,
      down: s.revenuePriceUpDown,
      same: s.revenuePriceUpSame,
    },
    down: {
      up: s.revenuePriceDownUp,
      down: s.revenuePriceDownDown,
      same: s.revenuePriceDownSame,
    },
  };
  const number = (v: number) => minus(fmt(v, { maximumFractionDigits: 2 }));
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{s.revenueHeading}</h3>
      <p className="font-mono text-foreground text-sm">
        {s.revenueMeasure}: {number(tr.before)} → {number(tr.after)} (
        {s.revenueChange}: {number(tr.change)})
      </p>
      <p className="text-foreground text-sm">
        {sentences[priceUp ? "up" : "down"][tr.direction]}
      </p>
    </div>
  );
}

function PointElasticities({
  data,
  fmt,
  strings: s,
}: {
  data: Data;
  fmt: Fmt;
  strings: ElasticityWorkingStrings;
}) {
  const slope = (data.q2 - data.q1) / (data.x2 - data.x1);
  const number = (v: number) => minus(fmt(v, { maximumFractionDigits: 4 }));
  const rows = [
    { key: "start", template: s.pointStart, p: data.x1, q: data.q1 },
    {
      key: "mid",
      template: s.pointMid,
      p: (data.x1 + data.x2) / 2,
      q: (data.q1 + data.q2) / 2,
    },
    { key: "end", template: s.pointEnd, p: data.x2, q: data.q2 },
  ];
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{s.pointHeading}</h3>
      <p className="text-foreground text-sm">
        {t(s.pointIntro, { slope: number(slope) })}
      </p>
      <ul className="space-y-1 font-mono text-foreground text-sm">
        {rows.map((row) => {
          const e = pointElasticity(slope, row.p, row.q);
          return (
            <li key={row.key}>
              {e === null
                ? s.pointUndefined
                : t(row.template, {
                    slope: number(slope),
                    p: number(row.p),
                    q: number(row.q),
                    e: number(e),
                  })}
            </li>
          );
        })}
      </ul>
      <p className="text-muted-foreground text-sm">{s.pointNote}</p>
    </div>
  );
}

export function ElasticityWorking({
  data,
  fmt,
  measure,
  result,
  strings: s,
  symbol,
}: {
  data: Data;
  fmt: Fmt;
  measure: Measure;
  result: ArcResult;
  strings: ElasticityWorkingStrings;
  symbol: string;
}) {
  const number = (v: number) => minus(fmt(v, { maximumFractionDigits: 4 }));
  const percent = (v: number) =>
    minus(fmt(v, { style: "percent", maximumFractionDigits: 2 }));
  const verdict = verdictFor(measure, result.elasticity, s, fmt);
  const headline = {
    price: s.elasticityPrice,
    income: s.elasticityIncome,
    cross: s.elasticityCross,
  }[measure];
  const driverChange = {
    price: s.percentChangePrice,
    income: s.percentChangeIncome,
    cross: s.percentChangeCross,
  }[measure];
  const wrongWay = measure === "price" && result.elasticity > 0;
  return (
    <div className="space-y-5">
      <StatGrid>
        <StatCard emphasis label={headline} value={number(result.elasticity)} />
        {measure === "price" ? (
          <StatCard
            label={s.absoluteValue}
            value={fmt(Math.abs(result.elasticity), {
              maximumFractionDigits: 4,
            })}
          />
        ) : null}
        <StatCard label={s.percentChangeQ} value={percent(result.percentQ)} />
        <StatCard label={driverChange} value={percent(result.percentX)} />
        <StatCard label={s.verdictLabel} value={verdict.name} />
      </StatGrid>
      <p className="text-foreground text-sm">{verdict.explain}</p>
      {wrongWay ? (
        <p className="text-muted-foreground text-sm">{s.positiveNote}</p>
      ) : null}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{s.workingHeading}</h3>
        <ul className="space-y-1 font-mono text-foreground text-sm">
          <li>
            {t(s.workQ, {
              q1: number(data.q1),
              q2: number(data.q2),
              dq: number(result.changeQ),
              mq: number(result.meanQ),
              pq: percent(result.percentQ),
            })}
          </li>
          <li>
            {t(s.workX, {
              sym: symbol,
              x1: number(data.x1),
              x2: number(data.x2),
              dx: number(result.changeX),
              mx: number(result.meanX),
              px: percent(result.percentX),
            })}
          </li>
          <li>
            {t(s.workE, {
              sym: symbol,
              pq: percent(result.percentQ),
              px: percent(result.percentX),
              e: number(result.elasticity),
            })}
          </li>
        </ul>
        <p className="text-muted-foreground text-xs">{s.roundedNote}</p>
      </div>
      {measure === "price" ? (
        <>
          <RevenueTest data={data} fmt={fmt} strings={s} />
          <PointElasticities data={data} fmt={fmt} strings={s} />
        </>
      ) : null}
    </div>
  );
}
